<template>
	<div class="card shadow-sm">
		<div class="card-header">
			<ul class="nav nav-tabs card-header-tabs">
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'media' }" href="#" @click.prevent="activeTab = 'media'">
						<i class="fas fa-file-pdf me-1"></i>
						Media Library
						<span v-if="mediaFiles.length" class="badge bg-secondary ms-1">{{ mediaFiles.length }}</span>
					</a>
				</li>
				<li class="nav-item">
					<a class="nav-link" :class="{ active: activeTab === 'meetings' }" href="#" @click.prevent="activeTab = 'meetings'">
						<i class="fas fa-calendar-alt me-1"></i>
						Meeting Documents
						<span v-if="meetingStats.total_documents" class="badge bg-secondary ms-1">{{ meetingStats.total_documents }}</span>
					</a>
				</li>
			</ul>
		</div>
		<div class="card-body">
			<!-- Media Library Tab -->
			<div v-if="activeTab === 'media'">
				<div v-if="loadingMedia" class="text-center py-4">
					<i class="fas fa-spinner fa-spin me-2"></i> Loading media library...
				</div>
				<div v-else-if="mediaFiles.length === 0" class="text-center text-muted py-4">
					No PDF files found in this client's media library.
				</div>
				<div v-else>
					<div class="d-flex justify-content-between align-items-center mb-3">
						<small class="text-muted">{{ mediaFiles.length }} PDF files</small>
						<button class="btn btn-sm btn-primary" @click="scanAllMedia" :disabled="scanningMedia">
							<i class="fas fa-search me-1"></i>
							{{ scanningMedia ? 'Scanning...' : 'Scan All Unscanned' }}
						</button>
					</div>
					<div class="table-responsive">
						<table class="table table-sm table-hover">
							<thead>
								<tr>
									<th>File Name</th>
									<th>Size</th>
									<th>Status</th>
									<th>Risk</th>
									<th>Issues</th>
									<th>Scanned</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="file in mediaFiles" :key="file.id">
									<td>
										<a :href="'https://storage.googleapis.com/juniper-media-library/' + file.path" target="_blank" class="text-decoration-none">
											{{ file.name }}
											<i class="fas fa-external-link-alt ms-1 small"></i>
										</a>
									</td>
									<td><small class="text-muted">{{ formatSize(file.size) }}</small></td>
									<td>
										<span v-if="file.scan_status === 'scanned'" class="badge bg-success">Scanned</span>
										<span v-else class="badge bg-secondary">Not scanned</span>
									</td>
									<td>
										<span v-if="file.risk_level" class="badge" :class="riskBadgeClass(file.risk_level)">{{ file.risk_level }}</span>
									</td>
									<td>
										<small v-if="file.scan_status === 'scanned'">
											<span v-if="file.critical_issues" class="badge bg-danger me-1">{{ file.critical_issues }} critical</span>
											<span v-if="file.major_issues" class="badge bg-warning text-dark me-1">{{ file.major_issues }} major</span>
											<span v-if="file.minor_issues" class="badge bg-info me-1">{{ file.minor_issues }} minor</span>
											<span v-if="!file.critical_issues && !file.major_issues && !file.minor_issues" class="badge bg-success">Clean</span>
										</small>
									</td>
									<td><small class="text-muted">{{ file.scanned_at ? formatDate(file.scanned_at) : '' }}</small></td>
									<td class="text-nowrap">
										<button
											v-if="file.scan_status === 'scanned' && (file.critical_issues || file.major_issues || file.minor_issues)"
											class="btn btn-sm btn-outline-info me-1"
											@click="showDetail(file)"
											title="View issues"
										>
											<i class="fas fa-list"></i>
										</button>
										<small>
											<span v-if="file.critical_issues" class="text-danger me-1">{{ file.critical_issues }}C</span>
											<span v-if="file.major_issues" class="text-warning me-1">{{ file.major_issues }}M</span>
											<span v-if="file.minor_issues" class="text-info">{{ file.minor_issues }}m</span>
											<span v-if="!file.critical_issues && !file.major_issues && !file.minor_issues" class="text-success">Clean</span>
										</small>
									</td>
									<td><small class="text-muted">{{ file.scanned_at ? formatDate(file.scanned_at) : '' }}</small></td>
									<td>
										<button
											class="btn btn-sm btn-outline-primary"
											@click="scanMediaFile(file)"
											:disabled="file.scanning"
											title="Scan"
										>
											<i :class="file.scanning ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- Meeting Documents Tab -->
			<div v-if="activeTab === 'meetings'">
				<div v-if="loadingMeetings" class="text-center py-4">
					<i class="fas fa-spinner fa-spin me-2"></i> Loading meeting documents...
				</div>
				<div v-else-if="!hasJurisdiction" class="alert alert-info">
					<i class="fas fa-info-circle me-2"></i>
					This client does not have HeyGov configured. No meeting documents available.
				</div>
				<div v-else-if="meetings.length === 0" class="text-center text-muted py-4">
					No meetings found for this client.
				</div>
				<div v-else>
					<div class="d-flex justify-content-between align-items-center mb-3">
						<small class="text-muted">
							{{ meetingStats.total_documents }} documents
							({{ meetingStats.pdf_count }} PDF, {{ meetingStats.doc_count }} DOC/DOCX)
							- {{ meetingStats.scanned }} scanned
						</small>
						<button class="btn btn-sm btn-primary" @click="scanAllMeetings" :disabled="scanningMeetings">
							<i class="fas fa-search me-1"></i>
							{{ scanningMeetings ? 'Scanning...' : 'Scan All Unscanned' }}
						</button>
					</div>
					<div v-if="scanningMeetings && scanProgress" class="mb-3">
						<div class="progress" style="height:6px;">
							<div class="progress-bar" :style="{ width: scanProgress + '%' }"></div>
						</div>
						<small class="text-muted">{{ scanProgressText }}</small>
					</div>
					<div class="table-responsive">
						<table class="table table-sm">
							<thead>
								<tr>
									<th>Meeting</th>
									<th>Document</th>
									<th>Type</th>
									<th>Status</th>
									<th>Risk</th>
									<th>Issues</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								<template v-for="meeting in meetings">
									<tr v-for="(doc, di) in meeting.documents" :key="meeting.id + '-' + di">
										<td v-if="di === 0" :rowspan="meeting.documents.length" class="align-middle">
											<strong>{{ meeting.title }}</strong>
											<br><small class="text-muted">{{ formatDate(meeting.date) }}</small>
										</td>
										<td>
											<a :href="doc.url" target="_blank" class="text-decoration-none">
												{{ doc.file_name }}
												<i class="fas fa-external-link-alt ms-1 small"></i>
											</a>
										</td>
										<td>
											<span class="badge bg-info me-1">{{ doc.type }}</span>
											<span v-if="doc.is_doc" class="badge bg-warning text-dark">{{ doc.file_extension.toUpperCase() }}</span>
											<span v-else class="badge bg-light text-dark">PDF</span>
											<br>
											<small v-if="doc.converted_from" class="text-muted">
												<i class="fas fa-exchange-alt me-1"></i>Converted to PDF
											</small>
										</td>
										<td>
											<span v-if="doc.scan_status === 'scanned'" class="badge bg-success">Scanned</span>
											<span v-else class="badge bg-secondary">Not scanned</span>
										</td>
										<td>
											<span v-if="doc.risk_level" class="badge" :class="riskBadgeClass(doc.risk_level)">{{ doc.risk_level }}</span>
										</td>
										<td>
											<small v-if="doc.scan_status === 'scanned'">
												<span v-if="doc.critical_issues" class="badge bg-danger me-1">{{ doc.critical_issues }} critical</span>
												<span v-if="doc.major_issues" class="badge bg-warning text-dark me-1">{{ doc.major_issues }} major</span>
												<span v-if="doc.minor_issues" class="badge bg-info me-1">{{ doc.minor_issues }} minor</span>
												<span v-if="!doc.critical_issues && !doc.major_issues && !doc.minor_issues" class="badge bg-success">Clean</span>
											</small>
										</td>
										<td class="text-nowrap">
											<button
												v-if="doc.scan_status === 'scanned' && (doc.critical_issues || doc.major_issues || doc.minor_issues)"
												class="btn btn-sm btn-outline-info me-1"
												@click="showDetail(doc)"
												title="View issues"
											>
												<i class="fas fa-list"></i>
											</button>
											<small>
												<span v-if="doc.critical_issues" class="text-danger me-1">{{ doc.critical_issues }}C</span>
												<span v-if="doc.major_issues" class="text-warning me-1">{{ doc.major_issues }}M</span>
												<span v-if="doc.minor_issues" class="text-info">{{ doc.minor_issues }}m</span>
												<span v-if="!doc.critical_issues && !doc.major_issues && !doc.minor_issues" class="text-success">Clean</span>
											</small>
										</td>
										<td>
											<button
												class="btn btn-sm btn-outline-primary"
												@click="scanMeetingDoc(doc)"
												:disabled="doc.scanning"
											>
												<i :class="doc.scanning ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
											</button>
										</td>
									</tr>
								</template>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>

		<!-- Issue Detail Modal -->
		<div v-if="detailDoc" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);" @click.self="detailDoc = null">
			<div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<div>
							<h5 class="modal-title">
								<i class="fas fa-file-alt me-2"></i>
								PDF Accessibility Issues
							</h5>
							<small class="text-muted">{{ detailDoc.file_name }}</small>
						</div>
						<button type="button" class="btn-close" @click="detailDoc = null"></button>
					</div>
					<div class="modal-body">
						<div class="d-flex gap-2 mb-3">
							<span v-if="detailDoc.risk_level" class="badge fs-6" :class="riskBadgeClass(detailDoc.risk_level)">{{ detailDoc.risk_level }}</span>
							<span v-if="detailDoc.converted_from" class="badge bg-warning text-dark">Converted from {{ detailDoc.converted_from.toUpperCase() }}</span>
						</div>
						<p v-if="detailDoc._summary" class="text-muted mb-3">{{ detailDoc._summary }}</p>

						<div v-if="detailDoc._issues && detailDoc._issues.length">
							<div
								v-for="(issue, i) in detailDoc._issues"
								:key="i"
								class="border rounded p-3 mb-2"
								:class="issue.severity === 'critical' ? 'border-danger' : issue.severity === 'major' ? 'border-warning' : 'border-info'"
							>
								<div class="d-flex justify-content-between align-items-start">
									<div>
										<span class="badge me-2" :class="issue.severity === 'critical' ? 'bg-danger' : issue.severity === 'major' ? 'bg-warning text-dark' : 'bg-info'">
											{{ issue.severity }}
										</span>
										<strong>{{ issue.title }}</strong>
										<span v-if="issue.occurrences > 1" class="badge bg-secondary ms-1">x{{ issue.occurrences }}</span>
									</div>
								</div>
								<p class="mb-1 mt-2">{{ issue.plain_english || issue.description }}</p>
								<div v-if="issue.how_to_fix" class="mt-2 p-2 bg-light rounded">
									<small><strong>How to fix:</strong> {{ issue.how_to_fix }}</small>
								</div>
								<div v-if="issue.error_details && issue.error_details.length" class="mt-2">
									<small class="text-muted d-block mb-1">Details ({{ issue.error_details.length }}):</small>
									<div v-for="(detail, di) in issue.error_details.slice(0, 5)" :key="di" class="small text-muted ps-2 border-start">
										{{ detail.message }}
									</div>
									<small v-if="issue.error_details.length > 5" class="text-muted">+{{ issue.error_details.length - 5 }} more</small>
								</div>
							</div>
						</div>
						<div v-else class="text-center text-muted py-3">
							No detailed issue data available. Try re-scanning this document.
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" @click="detailDoc = null">Close</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import api from '@/api'

export default {
	name: 'ClientDocumentTabs',
	props: {
		clientId: { type: [String, Number], required: true },
	},
	data() {
		return {
			activeTab: 'media',
			// Media library
			mediaFiles: [],
			loadingMedia: true,
			scanningMedia: false,
			// Meetings
			meetings: [],
			meetingStats: { total_documents: 0, scanned: 0, not_scanned: 0, pdf_count: 0, doc_count: 0 },
			loadingMeetings: true,
			hasJurisdiction: true,
			scanningMeetings: false,
			scanProgress: 0,
			scanProgressText: '',
			// Detail modal
			detailDoc: null,
		}
	},
	mounted() {
		this.loadMediaFiles()
		this.loadMeetings()
	},
	methods: {
		async loadMediaFiles() {
			this.loadingMedia = true
			try {
				const response = await api.get(`/juniper/ada-auditor/pdf/clients/${this.clientId}/results`)
				if (response && response.data && response.data.data) {
					this.mediaFiles = response.data.data.map(f => ({ ...f, scanning: false }))
				}
			} catch (error) {
				console.error('Failed to load media files:', error)
			} finally {
				this.loadingMedia = false
			}
		},

		async loadMeetings() {
			this.loadingMeetings = true
			try {
				const response = await api.get(`/juniper/ada-auditor/clients/${this.clientId}/meetings`)
				if (response && response.data) {
					if (response.data.message && response.data.message.includes('No HeyGov')) {
						this.hasJurisdiction = false
					} else {
						this.meetings = response.data.meetings || []
						this.meetingStats = response.data.stats || this.meetingStats
						// Add scanning state to each document
						this.meetings.forEach(m => {
							m.documents.forEach(d => { d.scanning = false })
						})
					}
				}
			} catch (error) {
				console.error('Failed to load meetings:', error)
			} finally {
				this.loadingMeetings = false
			}
		},

		async scanMediaFile(file) {
			file.scanning = true
			try {
				const pathParts = file.path.split('/')
				const encodedParts = pathParts.map(p => encodeURIComponent(p))
				const encodedPath = encodedParts.join('/')
				const url = 'https://storage.googleapis.com/juniper-media-library/' + encodedPath

				const response = await api.post('/juniper/ada-auditor/pdf/scan-url', {
					url: url,
					filename: file.name,
					file_id: file.id,
					client_id: this.clientId,
					file_path: file.path,
				})

				if (response && response.data && response.data.success) {
					const result = response.data.data
					file.scan_status = 'scanned'
					file.risk_level = result.risk_level
					file.critical_issues = result.issue_count?.critical || 0
					file.major_issues = result.issue_count?.major || 0
					file.minor_issues = result.issue_count?.minor || 0
					file.scanned_at = new Date().toISOString()
				}
			} catch (error) {
				console.error('Failed to scan file:', error)
				if (this.$toast) this.$toast.error('Failed to scan file')
			} finally {
				file.scanning = false
			}
		},

		async scanAllMedia() {
			this.scanningMedia = true
			const unscanned = this.mediaFiles.filter(f => f.scan_status !== 'scanned')
			for (const file of unscanned) {
				await this.scanMediaFile(file)
			}
			this.scanningMedia = false
			if (this.$toast) this.$toast.success(`Scanned ${unscanned.length} files`)
		},

		async scanMeetingDoc(doc) {
			doc.scanning = true
			try {
				const response = await api.post(`/juniper/ada-auditor/clients/${this.clientId}/meetings/scan`, {
					file_url: doc.url,
					file_path: doc.file_path,
				})

				if (response && response.data && response.data.success) {
					const result = response.data.data
					doc.scan_status = 'scanned'
					doc.risk_level = result.risk_level
					doc.critical_issues = result.issue_count?.critical || 0
					doc.major_issues = result.issue_count?.major || 0
					doc.minor_issues = result.issue_count?.minor || 0
					doc.converted_from = result.converted_from || null
					doc.scanned_at = new Date().toISOString()
					this.meetingStats.scanned++
					this.meetingStats.not_scanned--
				}
			} catch (error) {
				console.error('Failed to scan meeting doc:', error)
				if (this.$toast) this.$toast.error('Failed to scan document')
			} finally {
				doc.scanning = false
			}
		},

		async scanAllMeetings() {
			this.scanningMeetings = true
			this.scanProgress = 0

			try {
				const response = await api.post(`/juniper/ada-auditor/clients/${this.clientId}/meetings/scan-all`)
				if (response && response.data) {
					if (this.$toast) this.$toast.success(response.data.message || 'Scan complete')
					// Reload to get updated results
					await this.loadMeetings()
				}
			} catch (error) {
				console.error('Failed to scan all meetings:', error)
				if (this.$toast) this.$toast.error('Failed to scan meeting documents')
			} finally {
				this.scanningMeetings = false
				this.scanProgress = 0
			}
		},

		async showDetail(doc) {
			// For media library files, we have scan_result in the response
			// For meeting docs, we need to fetch it
			if (doc.scan_result) {
				this.detailDoc = {
					...doc,
					_issues: doc.scan_result.issues || [],
					_summary: doc.scan_result.summary || doc.summary || '',
				}
			} else {
				// Fetch full scan result from backend
				try {
					const source = doc.file_id ? 'juniper' : 'heygov'
					const identifier = doc.file_id ? String(doc.file_id) : doc.file_path
					const response = await api.get('/juniper/ada-auditor/pdf/scan-result', {
						params: { source, source_identifier: identifier }
					})
					if (response && response.data && response.data.scan_result) {
						this.detailDoc = {
							...doc,
							_issues: response.data.scan_result.issues || [],
							_summary: response.data.scan_result.summary || response.data.summary || '',
						}
					} else {
						this.detailDoc = { ...doc, _issues: [], _summary: doc.summary || 'No detail available' }
					}
				} catch (error) {
					this.detailDoc = { ...doc, _issues: [], _summary: 'Failed to load details' }
				}
			}
		},

		formatSize(bytes) {
			if (!bytes) return '-'
			const num = parseInt(bytes)
			if (isNaN(num)) return bytes
			if (num < 1024) return num + ' B'
			if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB'
			return (num / (1024 * 1024)).toFixed(1) + ' MB'
		},

		formatDate(dateStr) {
			if (!dateStr) return ''
			const d = new Date(dateStr)
			return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},

		riskBadgeClass(level) {
			const map = {
				compliant: 'bg-success',
				low: 'bg-success',
				medium: 'bg-warning text-dark',
				high: 'bg-danger',
				critical: 'bg-danger',
				unknown: 'bg-secondary',
			}
			return map[level] || 'bg-secondary'
		},
	},
}
</script>
